import { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { useReactiveVar } from '@apollo/client';
import { ViewMode, MasonryBreakpointCols, MasonryBreakpointColsList } from '@/config/constants';
import { districtVar } from '@/graphql/state';

type ViewModeContextType = {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  masonryBreakpointCols: typeof MasonryBreakpointCols | typeof MasonryBreakpointColsList;
};

export const LIST_MODE_DISTRICTS = [
  9719, 50132, 13059, 50019, 35308, 12885, 35410, 35520, 50124, 35346, 35354, 3286, 35309, 826,
  14404, 33179, 30780, 50135, 11879, 50215, 14217, 50120, 35465, 50068, 8676, 50254, 35411, 35561,
  50033, 9517, 35306, 35451, 5296, 35468, 3466, 35375, 13818, 2201, 35546, 33074, 1313, 50237, 3655,
  50161, 32453
];

const VIEW_MODE_STORAGE_KEY = 'viewMode';
const VIEW_MODE_DEFAULT_RESET_KEY = 'viewModeDefaultReset';

const ViewModeContext = createContext<ViewModeContextType>({
  viewMode: 'grid',
  setViewMode: () => null,
  masonryBreakpointCols: MasonryBreakpointCols,
});

type ViewModeProviderProps = {
  children: ReactNode;
};

export const ViewModeProvider: React.FC<ViewModeProviderProps> = ({ children }) => {
  const district = useReactiveVar(districtVar);
  const getViewMode = (): ViewMode => {
    const viewMode = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return (viewMode as ViewMode) ?? 'grid';
  };

  const [viewMode, setViewModeState] = useState<ViewMode>(getViewMode());

  const setViewMode = (newViewMode: ViewMode) => {
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, newViewMode);
    setViewModeState(newViewMode);
  };


  useEffect(() => {
    if (!district || localStorage.getItem(VIEW_MODE_DEFAULT_RESET_KEY)) return;

    const defaultViewMode: ViewMode = LIST_MODE_DISTRICTS.includes(Number(district.districtId))
      ? 'list'
      : 'grid';

    localStorage.setItem(VIEW_MODE_DEFAULT_RESET_KEY, 'true');
    setViewMode(defaultViewMode);
  }, [district]);

  const masonryBreakpointCols = useMemo(() => {
    return viewMode === 'list' ? MasonryBreakpointColsList : MasonryBreakpointCols;
  }, [viewMode]);

  return (
    <ViewModeContext.Provider value={{ viewMode, setViewMode, masonryBreakpointCols }}>
      {children}
    </ViewModeContext.Provider>
  );
};

export const useViewModeContext = () => {
  const context = useContext(ViewModeContext);
  if (!context) {
    throw new Error('useViewModeContext must be used within a ViewModeProvider');
  }
  return context;
};

export default ViewModeContext;
